import { DomHelpers } from '../core/DomHelpers.js';
import { getMainValue, parseBraceOrCsv, setMainValue } from '../core/EncodeValues.js';
import { on, off } from '../core/Events.js';
import { ImSys } from '../core/ImSys.js';
import { Widget } from '../core/Widget.js';
import { ViewWidgetHelper } from '../view/ViewWidgetHelper.js';
import { CmdPanelWidget } from '../widgets/CmdPanelWidget.js';
import { ApplyButtonController } from './ApplyButtonController.js';
import { FilterBadgeManager } from './FilterBadgeManager.js';
import { FilterChipsManager } from './FilterChipsManager.js';
import { FilterCollapsibleManager } from './FilterCollapsibleManager.js';
import { FilterDomManager } from './FilterDomManager.js';
import { resolveOwner, resolveFilterOwnerAdapter } from './FilterOwner.js';
import { FilterUiCommandProvider } from './FilterUiCommandProvider.js';

// search-core/FilterWidget.ts
/**
 * Виджет фильтрации данных.
 * Работает поверх готовой разметки или по схеме.
 *
 * Схема разметки элементов фильтра (читается в FilterDomManager)
 * 1) разметка на уровне атрибутов элементов фильтров (data-*):
 *  - fkey - ключ фильтра, как он будет отображаться в адресной строке
 *  - viewkey
 *  - fop - операторы, поддерживаемые фильтром (FilterOperators - больше, меньше, содержит и тп)
 *  - fvis - визуализация активных фильтров, буквенные флаги - 'c' - показывать в чипсах, 'b' - учитывать бейдж.
 *      Что бы отключить все, можно указать любой символ - по соглашению использовать 'x' (отключет отображениею фильтра в чипсах и бейдже)
 *  - fdefault - значение по умолчанию - это значение не будет кодироваться в поисковой строке
 *  - ftitle - название фильта для чипсов
 */
class FilterWidget extends Widget {
    constructor() {
        super(...arguments);
        /**
         *  Контейнер для отображения выбранных фильтров (чипы);
         * если null — чипы не используем
         */
        this._chipsHost = null;
        /**
         * Контейнер для отображения количества выбранных фильтров (бейдж);
         * если null — бейдж не используем
         */
        this._badgeHost = null;
        // последнее “применённое” (успешное apply) состояние
        this._appliedEntries = [];
        this._appliedSig = null;
        this._forceDomVisuals = false;
        /** флаги и кэш для коалесcинга */
        this._applyRunning = false; // сейчас выполняется apply-цикл
        this._applyDirty = false; // за время выполнения пришли новые изменения
        this._inflightSig = null; // сигнатура, которая сейчас «в полёте»
        this._lastVisualSig = null;
        /** Счётчик приостановленных реакций DOM. */
        this._suspendDomReaction = 0;
    }
    /** Подключение UI-команд */
    static _uiCommandProviders() {
        return new FilterUiCommandProvider();
    }
    /** Хост владельца, для которого этот фильтр применяет значения. */
    get ownerHost() {
        return this._owner;
    }
    /** Работа с dom фильтра */
    get dom() {
        return this._domManager;
    }
    /** Инициализация. */
    _create(options) {
        this._filterGroup = options.group ?? 'default';
        this._onFilterClose = options.onFilterClose;
        this._ui = ImSys.tmp_id++;
        this._visualsMode = options.visualsMode ?? "applied";
        // Определяем владельца фильтра и адаптер
        this._owner = resolveOwner(this.element, options, 'FilterWidget');
        this._ownerAdapter = resolveFilterOwnerAdapter(this._owner, options.ownerAdapter, "FilterWidget");
        // Подбираем контейнер для чипов и бейджа
        this._chipsHost = this._resolveChipsHost(options, this._ownerAdapter, this._owner);
        // Активируем утилиты работы с разметкой
        this._domManager = options.testing?.domManager
            ?? new FilterDomManager(this.element, { prefix: options.prefix });
        if (this._chipsHost) {
            if (options.testing?.chipsMgr === null) {
                this._chipsMgr = undefined;
            }
            else if (options.testing?.chipsMgr) {
                this._chipsMgr = options.testing.chipsMgr;
            }
            else if (this._chipsHost) {
                this._chipsMgr = FilterChipsManager.getOrInit(this._chipsHost);
            }
        }
        this._badgeHost = this._resolveBadgeHost(options, this._ownerAdapter, this._owner, this.element);
        if (this._badgeHost) {
            // badgeHost общий → используем агрегатор
            this._badgeMgr = FilterBadgeManager.getOrInit(this._badgeHost);
        }
        // ← находим связанную командную панель (если есть)
        this._cmdPanel = this._resolveCmdPanel(options, this._ownerAdapter, this._owner);
        // Подключить управление фильтром, закрытие панели, управление группами и т.п.
        this._bindEvents(options.closeOnOutsideClick);
        // ✨ первичная подсветка по текущему состоянию DOM
        try {
            const initialEntries = this._domManager.collectEntries();
            const initialValues = this._activeValues(initialEntries);
            this._domManager.updateLabelHighlight(initialValues);
            // считаем, что стартовое состояние “применено”
            this._appliedEntries = initialEntries;
            this._appliedSig = signature(initialValues);
            // визуал рисуем по выбранному режиму
            this._updateVisuals(initialEntries);
        }
        catch { /* noop */ }
        this._initCollapsible();
    }
    /** Активные значения фильтра */
    _activeValues(entries) {
        return entries
            .filter((e) => !!e.value)
            .map(e => e.value);
    }
    /** Значения для чипсов */
    _chipValues(entries) {
        return entries
            .filter(e => !!e.value)
            .filter(e => e.schema.showInChips !== false);
    }
    /** Значения для бейджа */
    _badgeCount(entries) {
        return entries
            .filter(e => !!e.value)
            .filter(e => e.schema.showInBadge !== false)
            .length;
    }
    /** Логическая группа фильтра (если нужно для агрегации). */
    get filterGroup() {
        return this._filterGroup;
    }
    /** Найти связанную командную панель (если есть) */
    _resolveCmdPanel(options, adapter, owner) {
        // 1) явный host из опций
        let host = options.cmdPanelHost ??
            // 2) поиск по маске из опций
            (options.cmdPanelHostMask
                ? owner.querySelector(options.cmdPanelHostMask) ??
                    this.element.closest(options.cmdPanelHostMask)
                : null) ??
            // 3) из адаптера
            adapter.cmdPanelHost ??
            undefined;
        if (!host && owner.classList.contains('im-table')) {
            // 4) fallback: для таблиц
            host = owner.querySelector(".im-toolstrip") ?? undefined;
        }
        if (!host)
            return undefined;
        // 5) ищем живой экземпляр CmdPanelWidget, чей element совпадает/лежит внутри host
        return Widget.getOn(host, CmdPanelWidget);
    }
    /**
     * Определяет контейнер для чипов выбранных фильтров по приоритетам:
     * 1) Явно из опций (chipsHost)
     * 2) По маске из опций (chipsHostMask)
     * 3) Из адаптера (adapter.chipsHost)
     * 4) Иначе — null (чипы не используем)
     */
    _resolveChipsHost(options, adapter, owner) {
        // 1) из опций, если передан явно
        if (options.chipsHost instanceof HTMLElement) {
            return options.chipsHost;
        }
        // 2) из опций по маске (если задана)
        if (options.chipsHostMask) {
            const byMask = owner.querySelector(options.chipsHostMask)
                ?? this.element.closest(options.chipsHostMask);
            if (byMask)
                return byMask;
        }
        // 3) из адаптера (если он её предоставляет)
        if (adapter && adapter.chipsHost instanceof HTMLElement) {
            return adapter.chipsHost;
        }
        // 4) не найдено — чипы не используем
        return null;
    }
    /**
     * Определяет контейнер для бейджа количества выбранных фильтров по приоритетам:
     * 1) Явно из опций (badgeHost)
     * 2) По маске из опций (badgeHostMask)
     * 3) Из адаптера (adapter.badgeHost)
     * 4) Fallback: ищем .im-table-filterscount в контейнере фильтра
     * 5) Иначе — null (бейдж не используем)
     */
    _resolveBadgeHost(options, adapter, owner, host) {
        // 1) из опций
        if (options.badgeHost instanceof HTMLElement) {
            return options.badgeHost;
        }
        // 2) из опций по маске
        if (options.badgeHostMask) {
            const byMask = owner.querySelector(options.badgeHostMask) ??
                this.element.closest(options.badgeHostMask);
            if (byMask)
                return byMask;
        }
        // 3) из адаптера
        if (adapter && adapter.badgeHost instanceof HTMLElement) {
            return adapter.badgeHost;
        }
        // 4) fallback ищем готовый узел .im-table-filterscount
        const el = this.element.querySelector(".im-table-filterscount");
        if (el)
            return el;
        // 5) не нашли — не используем бейдж
        return null;
    }
    /**
     * Читает значения из DOM и обновляет подсветку лейблов.
     * Ничего не рисует (чипсы/бейдж — отдельно, через _updateVisuals()).
     */
    _readDomAndHighlight() {
        const entries = this._domManager.collectEntries();
        const values = this._activeValues(entries);
        this._domManager.updateLabelHighlight(values);
        return {
            entries,
            sig: signature(values)
        };
    }
    /**
     * Обновляет чипсы/бейдж. Источник выбирается режимом:
     * - dom:    показываем текущее выбранное в DOM
     * - applied:показываем только подтверждённое (после успешного apply)
     */
    _updateVisuals(domEntries) {
        const entriesToShow = this._forceDomVisuals
            ? domEntries
            : (this._visualsMode === "applied" ? this._appliedEntries : domEntries);
        const chipValues = this._chipValues(entriesToShow);
        const badgeCount = this._badgeCount(entriesToShow);
        const visualSig = `${visualSignature(chipValues)}|${badgeCount}`;
        if (visualSig === this._lastVisualSig)
            return;
        this._updateBadge(badgeCount);
        this._chipsMgr?.render(chipValues, this._filterGroup);
        this._lastVisualSig = visualSig;
    }
    /**
     * Обработчик изменений DOM (change/combochange).
     * Если есть data-applymode — показываем Apply и не вызываем apply сразу.
     * Иначе — применяем фильтр сразу (через _applyFromDom с коалесcингом).
     * Для apply-режима data-key обязателен.
     */
    _onDomChanged(ev, prevent) {
        // при программных изменениях DOM игнорируем событие
        if (this._suspendDomReaction > 0)
            return;
        const raw = ev.detail?.sourceEl;
        const t = ev.target;
        const src = (raw instanceof HTMLElement ? raw : null) ??
            (t instanceof HTMLElement ? t : null);
        const target = t instanceof HTMLElement ? t : null;
        if (!src) {
            void this._applyFromDom();
            return;
        }
        // Предотвращаем ложные change от кастомных контролов
        if (prevent) {
            const h = src.closest('.dropdown, .im-combo')
                ?? target?.closest('.dropdown, .im-combo')
                ?? null;
            if (h)
                return;
        }
        // applymode: пробуем от src, если не вышло — от реального target
        const applyHost = src.closest('[data-applymode]') ??
            target?.closest('[data-applymode]') ??
            null;
        const isApplyMode = !!applyHost;
        if (!isApplyMode) {
            void this._applyFromDom();
            return;
        }
        // dk обязателен: если нет data-key — не делаем apply-режим (иначе неясно, что “копить”)
        const keyHost = src.closest('[data-key]') ??
            target?.closest('[data-key]') ??
            null;
        const dk = keyHost?.getAttribute('data-key');
        if (!dk) {
            void this._applyFromDom();
            return;
        }
        const { entries, sig } = this._readDomAndHighlight();
        // Рисуем визуал согласно режиму:
        // - dom: сразу по values
        // - applied: по _appliedValues (то есть визуально “не дернется”)
        this._updateVisuals(entries);
        // если по факту изменений нет — прячем Apply
        const alreadyApplied = sig === this._appliedSig;
        const alreadySent = sig === this._inflightSig;
        if (alreadyApplied || alreadySent) {
            this._applyBtn?.hide();
            return;
        }
        this._applyBtn ??= new ApplyButtonController(this.element, { className: "im-bt-apply" });
        this._applyBtn.showNear(keyHost ?? src, () => { void this._applyFromDom(); });
    }
    /** Подключить события изменения значений фильтра (делегирование на корневой элемент виджета) */
    _bindEvents(closeOnOutsideClick) {
        const ns = ".filter" + this._ui;
        // Стандартные элементы форм: input/select/textarea/checkbox/radio
        on(this.element, 'change' + ns, (ev) => this._onDomChanged(ev, true));
        // Кастомное событие от ComboBoxWidget 
        on(this.element, 'combochange' + ns, (ev) => this._onDomChanged(ev, false));
        // todo: Есть ли конфликт между ddchange и change
        // Кастомное событие от ComboBoxWidget 
        on(this.element, 'ddchange' + ns, (ev) => this._onDomChanged(ev, false));
        // События кликов по чипсам сброса выбранных фильтров
        if (this._chipsHost) {
            on(this._chipsHost, 'click' + ns, (e) => { this._handleChipsClick(e); });
        }
        // Закрытие фильтра при клике вне панели
        if (closeOnOutsideClick)
            on(document, 'mousedown' + ns, (e) => {
                // Игнорируем если уже не видно фильтра
                if (!DomHelpers.isVisible(this.element))
                    return;
                const target = e.target;
                if (!target)
                    return;
                // Проверяем: клик вне aside и не по кнопке фильтра
                if (!target.closest('.modal-aside') && !target.closest('.bt-filter')) {
                    DomHelpers.hide(this.element);
                    this._onFilterClose?.();
                }
            });
        // Нажатие Enter
        on(this.element, "keydown" + ns, (e) => {
            const el = e.target;
            if (el?.dataset.fkey) {
                const kev = e;
                if (kev.isComposing)
                    return;
                if (kev.key !== "Enter")
                    return;
                if (kev.shiftKey || kev.altKey || kev.ctrlKey || kev.metaKey)
                    return;
                kev.preventDefault();
                this._onDomChanged(e, false);
            }
        });
    }
    //** Закрытие фильтра */
    close() {
        DomHelpers.hide(this.element);
        this._onFilterClose?.();
    }
    /** Сброс всех фильтров */
    // правка clearAll()
    clearAll() {
        this._domManager.clearAll();
        // хотим сразу показать очистку даже в applied-режиме
        this._forceDomVisuals = true;
        const { entries } = this._readDomAndHighlight();
        this._updateVisuals(entries);
        this._applyBtn?.hide();
        void this._applyFromDom();
    }
    /** Осчистка фильтров указанных в чипсах */
    clearChips(ctx) {
        const entries = this._domManager.collectEntries();
        const groupId = (ctx && this._chipsMgr) ? this._chipsMgr.getChipsGroupId(ctx) : null;
        console.log('clearChips groupID', groupId);
        // берём только те, у которых есть value и они участвуют в чипсах
        const keys = Array.from(new Set(entries
            .filter(e => !!e.value)
            .filter(e => e.schema.showInChips !== false)
            .filter(e => {
            if (!groupId)
                return true; // глобальная кнопка -> все
            return (e.schema.chipsGroup ?? "default") === groupId;
        })
            .map(e => e.schema.key)));
        this.clearKeys(keys);
    }
    /** Клик по чипсу или "очистить всё" */
    _handleChipsClick(e) {
        const t = e.target;
        if (!t)
            return;
        // Очистить всё
        const clearAll = t.closest('.chips__clearall');
        if (clearAll) {
            this.clearChips(clearAll);
            return;
        }
        // Удаление одного чипса
        const item = t.closest('.chips__item');
        if (item && item.dataset.fkey) {
            const key = item.dataset.fkey;
            const one = item.dataset.item;
            if (one) {
                // array each: удаляем только один элемент
                this._removeArrayItem(key, one);
                void this._applyFromDom();
                return;
            }
            // summary / scalar / range: как раньше
            this._domManager.clearKey(key);
            this._domManager.clearKey(key + "__till");
            this._domManager.clearKey(key + "__type");
            void this._applyFromDom();
        }
    }
    //** Удаление чипсов из массива */
    _removeArrayItem(key, itemValue) {
        const fieldMap = this._domManager.collectFieldMap();
        const fm = fieldMap[key];
        if (!fm)
            return;
        const node = this.element.querySelector(`[data-key="${fm.dataKey}"]`);
        if (!node)
            return;
        const cur = getMainValue(node);
        // нормализуем к string[]
        const list = Array.isArray(cur)
            ? cur.map(x => String(x))
            : parseBraceOrCsv(cur).map(x => String(x));
        const next = list.filter(x => x !== itemValue);
        // если опустели — считаем “сброс”
        setMainValue(node, next.length > 0 ? next : null);
    }
    /** Собрать значения из DOM и применить фильтр у владельца (с коалесcингом) */
    async _applyFromDom() {
        this._applyDirty = true;
        if (this._applyRunning)
            return;
        this._applyRunning = true;
        try {
            while (this._applyDirty) {
                this._applyDirty = false;
                const { entries, sig } = this._readDomAndHighlight();
                // Обновить визуал по выбранному режиму
                // - dom: сразу отрисует values
                // - applied: покажет старое applied, пока apply не успешен
                this._updateVisuals(entries);
                // если логика не изменилась — пропускаем запрос к владельцу
                if (sig === this._appliedSig || sig === this._inflightSig) {
                    continue;
                }
                // фиксируем «в полёте» ДО await
                this._inflightSig = sig;
                const prevAppliedSig = this._appliedSig;
                const prevAppliedEntries = this._appliedEntries;
                try {
                    await this._ownerAdapter.apply();
                    this._appliedEntries = entries;
                    this._appliedSig = sig;
                    // завершили apply — возвращаем режим обратно
                    this._forceDomVisuals = false;
                    this._updateVisuals(entries);
                    this._applyBtn?.hide();
                }
                catch (err) {
                    this._appliedSig = prevAppliedSig;
                    this._appliedEntries = prevAppliedEntries;
                    // при ошибке тоже возвращаем режим обратно
                    this._forceDomVisuals = false;
                    this._updateVisuals(prevAppliedEntries);
                    throw err;
                }
                finally {
                    this._inflightSig = null;
                }
                // если за время await пришли новые изменения — цикл продолжится
            }
        }
        finally {
            this._applyRunning = false;
        }
    }
    // ── хелперы ─────────────────────────────────────────────────────
    _updateBadge(count) {
        // ✅ Если badge общий — получаем агрегированный total
        // ✅ Иначе — используем локальный count
        const total = this._badgeMgr
            ? this._badgeMgr.update(this._filterGroup, count)
            : count;
        // ✅ CmdPanel должен получать ТОТ ЖЕ total, что и badge
        try {
            this._cmdPanel?.setFilterCount(Math.max(0, total));
        }
        catch { /* noop */ }
    }
    /**
     * Применить внешнее состояние (обычно из URL) в DOM,
     * без вызова ownerAdapter.apply().
     */
    applyExternalState(stateByKey) {
        this._suspendDomReaction++;
        try {
            this._domManager.applyState?.(stateByKey);
            // URL считаем каноническим состоянием → фиксируем как applied
            const { entries, sig } = this._readDomAndHighlight();
            this._appliedEntries = entries;
            this._appliedSig = sig;
            this._forceDomVisuals = false;
            this._updateVisuals(entries);
            this._applyBtn?.hide();
            this._collapsible?.expandSectionsWithValues();
        }
        finally {
            this._suspendDomReaction--;
        }
    }
    /** Очистка указанных ключей */
    clearKeys(keys) {
        if (!keys || keys.length === 0)
            return;
        // очищаем только указанные ключи (с каскадом)
        for (const key of keys) {
            if (!key)
                continue;
            this._domManager.clearKey(key);
            this._domManager.clearKey(key + "__till");
            this._domManager.clearKey(key + "__type");
        }
        // хотим сразу показать очистку даже в applied-режиме
        this._forceDomVisuals = true;
        const { entries } = this._readDomAndHighlight();
        this._updateVisuals(entries);
        this._applyBtn?.hide();
        void this._applyFromDom();
    }
    //** Обновление зависимых элементов фильтра */
    reloadFields(keys) {
        if (!keys?.length)
            return;
        const fieldMap = this._domManager.collectFieldMap();
        const uniq = new Set();
        for (const key of keys) {
            if (!key)
                continue;
            const fm = fieldMap[key];
            if (!fm)
                continue;
            const dk = fm?.dataKey ?? key;
            if (!dk)
                continue;
            uniq.add(dk);
        }
        const uniqCsv = Array.from(uniq).join(",");
        if (!uniqCsv)
            return;
        const view = ViewWidgetHelper.closestOnByName(this.element);
        view?.reload(uniqCsv);
    }
    /**
     * Инициализация менеджера секций.
     * Вызывать после инициализации this.dom.
     */
    _initCollapsible() {
        this._collapsible = new FilterCollapsibleManager(this.element, this._domManager, {
            legendSelector: ".filter__legend_plus",
            legendActiveClass: "filter__legend_plus_active",
            wrapperSelector: ".filter__wrapper",
            animationMs: 300
        });
        this._collapsible.hydrate();
    }
    /** Очистка ресурсов. */
    _onDestroy() {
        // ✅ убираем вклад фильтра из агрегата и получаем новый total
        const total = this._badgeMgr ? this._badgeMgr.remove(this._filterGroup) : 0;
        // ✅ синхронизируем cmdPanel
        try {
            this._cmdPanel?.setFilterCount(Math.max(0, total));
        }
        catch { /* noop */ }
        const ns = ".filter" + this._ui;
        off(this.element, ns);
        if (this._chipsHost)
            off(this._chipsHost, ns);
        off(document, ns);
        this._collapsible?.destroy();
        this._collapsible = undefined;
    }
}
/** Имя виджета */
FilterWidget._alias = "Filter";
/** стабильная сигнатура: сортируем по key + value (valueTitle/label не влияют на запрос) */
function signature(values) {
    const core = values
        .map(v => ({ k: v.key, v: v.value })) // берём только то, что реально «влияет» на фильтр
        .sort((a, b) => (a.k === b.k ? (a.v < b.v ? -1 : a.v > b.v ? 1 : 0) : a.k < b.k ? -1 : 1));
    return JSON.stringify(core);
}
function visualSignature(entries) {
    const core = entries
        .filter((e) => !!e.value)
        .filter(e => e.schema.showInChips !== false)
        .map(e => {
        const v = e.value;
        const s = e.schema;
        return {
            // ключ фильтра (важно для стабильности)
            k: s.key ?? v.key,
            // группа чипсов тоже влияет на визуал (одни и те же значения могут быть в разных группах)
            g: (s.chipsGroup ?? "default"),
            // само значение
            v: v.value ?? "",
            // заголовок значения
            t: v.valueTitle ?? "",
            // label берём из schema (визуально это то, что показываем),
            // но оставим fallback на v.label для совместимости
            l: s.label ?? v.label ?? ""
        };
    })
        .sort((a, b) => {
        if (a.g !== b.g)
            return a.g < b.g ? -1 : 1;
        if (a.k !== b.k)
            return a.k < b.k ? -1 : 1;
        if (a.v !== b.v)
            return a.v < b.v ? -1 : 1;
        if (a.t !== b.t)
            return a.t < b.t ? -1 : 1;
        if (a.l !== b.l)
            return a.l < b.l ? -1 : 1;
        return 0;
    });
    return JSON.stringify(core);
}

export { FilterWidget, signature, visualSignature };
